/**
 * A folder on one of the person's computers, chosen for a host that asked (ADR 0106).
 *
 * Drawn by the frame, over the conversation and inside the frame, when a host sends
 * `chat42:pick-folder`. The person's own paired machines, each with whether it is online, then a
 * folder on the one they choose, walked through that machine exactly as Where walks one
 * (`browseMachine`), then the folder itself: where it is, which repository it is a checkout of
 * and on which branch, and whether that is the repository the host named. A folder that is not
 * is said so, in ochre because it is a question for the person, and can still be chosen: the
 * host's guess is a hint, and the person knows their own disk.
 *
 * **Small.** One dialog, three steps, nothing to configure. In a 420-point frame it is a sheet
 * off the bottom, as every dialog here is at that width.
 *
 * **Keyboard first.** Every step is buttons in reading order; the first thing worth pressing has
 * focus when a step opens; Escape closes from anywhere in it.
 *
 * **No product name.** It is drawn inside somebody else's page, which has its own (house rule
 * for a frame's copy). "This computer", "your computers", never whose software they run.
 *
 * Decides nothing about what the folder is used for. It hands back the place, and the host asks
 * for a run on it with `start-work`'s `code`.
 */
import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import type { Chat42Client } from '@chat42/client';
import { folderId, remoteLabel } from '@chat42/runtime/workspace';
import { themeStyle, type ThemeTokens } from './theme.ts';
import {
  pickClosed, recallFolder, refusedByMachine, rememberFolder, remoteMatches,
  type FolderHint, type FolderPickOutcome, type PickedFolder,
} from './host-folder.ts';

type Listing = Awaited<ReturnType<Chat42Client['browseMachine']>>;
type Device = Chat42Client['state']['devices'][number];

/** Where the picker is. */
type Step =
  | { at: 'machines' }
  | { at: 'folders'; device: Device; listing?: Listing; loading: boolean; error?: string }
  | { at: 'folder'; device: Device; path: string; checking: boolean; found?: Omit<PickedFolder, 'deviceId' | 'deviceName' | 'path'>; error?: string };

export interface FolderPickerProps {
  /** The signed-in person's client. Only their own machines are in its list. */
  client: Chat42Client;
  hint?: FolderHint;
  /** Who is choosing, for remembering where they chose last. None, and nothing is remembered. */
  userId?: string;
  /** Where "Pair this computer" opens, in a new tab. */
  pairUrl: string;
  theme?: ThemeTokens;
  /** Where the last choice is kept. The browser's local storage unless a test says otherwise. */
  storage?: Pick<Storage, 'getItem' | 'setItem'>;
  /** Called once, with how it closed. */
  onDone: (outcome: FolderPickOutcome) => void;
}

const nameOf = (path: string) => path.split('/').filter(Boolean).pop() ?? path;
/** The folder a path is in. Past the top of what a machine shows, it answers with its top instead. */
const parentOf = (path: string) => path.replace(/\/+$/, '').replace(/\/[^/]+$/, '') || '/';

export function FolderPicker(props: FolderPickerProps) {
  const { client, hint, userId, pairUrl, onDone } = props;
  const storage = props.storage ?? (typeof localStorage !== 'undefined' ? localStorage : undefined);
  const devices = useSyncExternalStore(
    (fn) => client.subscribe(fn), () => client.state.devices, () => client.state.devices,
  ).filter((d) => !d.revokedAt);

  const [loaded, setLoaded] = useState(false);
  const [step, setStep] = useState<Step>({ at: 'machines' });
  /** A machine said this person may not use it. Closing then tells the host so. */
  const [refused, setRefused] = useState(false);
  const done = useRef(false);
  const dialog = useRef<HTMLDivElement>(null);

  const finish = useCallback((outcome: FolderPickOutcome) => {
    if (done.current) return;
    done.current = true;
    onDone(outcome);
  }, [onDone]);

  const close = useCallback(() => {
    finish(pickClosed({ machines: devices.length, online: devices.filter((d) => d.connected).length, refused }));
  }, [finish, devices, refused]);

  const walk = useCallback(async (device: Device, path?: string, fallBack = false) => {
    setStep({ at: 'folders', device, loading: true });
    try {
      const listing = await client.browseMachine(device.id, path);
      setStep({ at: 'folders', device, listing, loading: false });
    } catch (err) {
      const message = (err as Error).message;
      // A remembered folder that has gone since: its machine's top instead, said by nothing,
      // because nothing went wrong that the person did.
      if (fallBack && path) { void walk(device); return; }
      if (refusedByMachine(message)) setRefused(true);
      setStep({ at: 'folders', device, loading: false, error: message });
    }
  }, [client]);

  // The list, fresh, and then where this person chose last for this hint, where that machine is up.
  useEffect(() => {
    let live = true;
    void client.loadDevices().finally(() => {
      if (!live) return;
      setLoaded(true);
      const last = recallFolder(storage, userId, hint);
      const device = last && client.state.devices.find((d) => d.id === last.deviceId && d.connected && !d.revokedAt);
      if (last && device) void walk(device, last.path, true);
    });
    return () => { live = false; };
    // Once per opening: the picker is mounted for one request.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The first thing worth pressing, focused as each step opens, so the keyboard is never lost.
  useEffect(() => {
    // The step's own first button, never the close in the header: Tab reaches that anyway.
    const first = dialog.current?.querySelector<HTMLElement>('[data-first]:not(:disabled)')
      ?? dialog.current?.querySelector<HTMLElement>('.c42-dialogbody button:not(:disabled)')
      ?? dialog.current?.querySelector<HTMLElement>('.c42-dialogfoot button:not(:disabled)');
    first?.focus();
  }, [step.at, loaded, step.at === 'folders' ? step.listing?.path : undefined]);

  const look = async (device: Device, path: string) => {
    setStep({ at: 'folder', device, path, checking: true });
    try {
      const check = await client.checkWorkspace({ workspaceId: folderId(device.id, path) });
      if (!check.ok) {
        if (check.case === 'not-yours') setRefused(true);
        setStep({ at: 'folder', device, path, checking: false, error: [check.said, check.next].filter(Boolean).join(' ') });
        return;
      }
      const remote = check.remember.repositoryUrl;
      const branch = check.branch
        ? (await client.machineBranches(device.id, path).catch(() => undefined))?.current
        : undefined;
      const matches = remoteMatches(remote, hint?.repository);
      setStep({
        at: 'folder', device, path, checking: false,
        found: { ...(remote ? { remote } : {}), ...(branch ? { branch } : {}), ...(matches !== undefined ? { matches } : {}) },
      });
    } catch (err) {
      setStep({ at: 'folder', device, path, checking: false, error: (err as Error).message });
    }
  };

  const choose = (device: Device, path: string, found: NonNullable<Extract<Step, { at: 'folder' }>['found']>) => {
    rememberFolder(storage, userId, hint, { deviceId: device.id, path });
    finish({ kind: 'picked', folder: { deviceId: device.id, deviceName: device.label, path, ...found } });
  };

  const online = devices.filter((d) => d.connected).length;

  return (
    <div className="c42 c42-fpick" style={themeStyle(props.theme)}>
      <div className="c42-modal-back centred" onClick={close}>
        <div
          ref={dialog}
          className="c42-dialog c42-fpick-dialog" role="dialog" aria-modal="true" aria-labelledby="c42-fpick-title"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); close(); } }}
        >
          <div className="c42-dialoghd">
            <div style={{ minWidth: 0 }}>
              <b id="c42-fpick-title">{step.at === 'machines' ? 'Choose a folder' : step.device.label}</b>
              {hint?.label && <p className="c42-why">{hint.label}</p>}
            </div>
            <button className="c42-icon" onClick={close} aria-label="Close">✕</button>
          </div>

          <div className="c42-dialogbody">
            {step.at === 'machines' && (
              !loaded ? (
                <p className="c42-shint" role="status">Looking for your computers…</p>
              ) : devices.length === 0 ? (
                <div className="c42-fpick-empty">
                  <p>No computer is paired yet.</p>
                  <p className="c42-shint">
                    Pair the computer the folder is on, and its folders can be opened from here.
                    Pairing opens in a new tab; come back here when it is done.
                  </p>
                  <div className="c42-acts">
                    <button
                      className="c42-mini primary" data-first
                      onClick={() => window.open(pairUrl, '_blank', 'noopener')}
                    >Pair this computer</button>
                    <button className="c42-mini" onClick={() => { setLoaded(false); void client.loadDevices().finally(() => setLoaded(true)); }}>
                      Check again
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  {hint?.repository && (
                    <p className="c42-shint">Looking for a checkout of <b>{remoteLabel(hint.repository)}</b>.</p>
                  )}
                  {online === 0 && (
                    <p className="c42-fpick-note" role="status">
                      None of your computers is online. Start the one with the folder, then check again.
                    </p>
                  )}
                  <ul className="c42-fpick-list" aria-label="Your computers">
                    {devices.map((d) => (
                      <li key={d.id}>
                        <button
                          className="c42-fpick-row" disabled={!d.connected}
                          {...(d.connected ? {} : { 'aria-describedby': `c42-fpick-off-${d.id}` })}
                          onClick={() => void walk(d)}
                        >
                          <span className="c42-fpick-name">{d.label}</span>
                          <span className={`c42-fpick-state${d.connected ? ' on' : ''}`} id={`c42-fpick-off-${d.id}`}>
                            {d.connected ? 'Online' : 'Offline'}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                  {online === 0 && (
                    <div className="c42-acts">
                      <button className="c42-mini" onClick={() => { setLoaded(false); void client.loadDevices().finally(() => setLoaded(true)); }}>
                        Check again
                      </button>
                    </div>
                  )}
                </>
              )
            )}

            {step.at === 'folders' && (
              <>
                <p className="c42-fpick-path" title={step.listing?.path}>{step.listing?.path ?? ' '}</p>
                {step.error && <p className="c42-fpick-note" role="alert">{step.error}</p>}
                {step.loading ? (
                  <p className="c42-shint" role="status">Opening…</p>
                ) : step.listing && (
                  <ul className="c42-fpick-list" aria-label={`Folders in ${nameOf(step.listing.path)}`}>
                    {step.listing.parent && (
                      <li>
                        <button className="c42-fpick-row" onClick={() => void walk(step.device, step.listing!.parent)}>
                          <span className="c42-fpick-name">↑ Up</span>
                        </button>
                      </li>
                    )}
                    {step.listing.entries.length === 0 && <li className="c42-shint">No folders in here.</li>}
                    {step.listing.entries.map((e) => (
                      <li key={e.path}>
                        <button className="c42-fpick-row" onClick={() => void walk(step.device, e.path)} aria-label={`Open ${e.name}`}>
                          <span className="c42-fpick-name">{e.name}</span>
                          {e.repo && <span className="c42-fpick-state">git</span>}
                        </button>
                        {e.repo && (
                          <button className="c42-mini" onClick={() => void look(step.device, e.path)} aria-label={`Choose ${e.name}`}>
                            Choose
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}

            {step.at === 'folder' && (
              <>
                <p className="c42-fpick-path" title={step.path}>{step.path}</p>
                {step.checking ? (
                  <p className="c42-shint" role="status">Looking at the folder…</p>
                ) : step.error ? (
                  <p className="c42-fpick-note" role="alert">{step.error}</p>
                ) : step.found && (
                  <>
                    <div className="c42-kv"><span>Repository</span><b>{step.found.remote ? remoteLabel(step.found.remote) : 'not a git checkout'}</b></div>
                    {step.found.branch && <div className="c42-kv"><span>Branch</span><b>{step.found.branch}</b></div>}
                    {step.found.matches === true && (
                      <p className="c42-shint" role="status">This is a checkout of {remoteLabel(hint!.repository!)}.</p>
                    )}
                    {step.found.matches === false && (
                      <p className="c42-fpick-note" role="status">
                        {step.found.remote
                          ? `This is a checkout of ${remoteLabel(step.found.remote)}, not ${remoteLabel(hint!.repository!)}.`
                          : `This is not a checkout of ${remoteLabel(hint!.repository!)}.`}
                        {' '}You can still use it.
                      </p>
                    )}
                  </>
                )}
              </>
            )}
          </div>

          <div className="c42-dialogfoot">
            {step.at === 'machines' && <button className="c42-mini" onClick={close}>Cancel</button>}
            {step.at === 'folders' && (
              <>
                <button className="c42-mini" onClick={() => setStep({ at: 'machines' })}>Your computers</button>
                <button
                  className="c42-mini primary" data-first={step.listing ? undefined : true}
                  disabled={!step.listing || step.loading}
                  onClick={() => step.listing && void look(step.device, step.listing.path)}
                >Use this folder</button>
              </>
            )}
            {step.at === 'folder' && (
              <>
                <button className="c42-mini" onClick={() => void walk(step.device, parentOf(step.path), true)}>Choose another</button>
                <button
                  className="c42-mini primary" data-first
                  disabled={step.checking || !step.found}
                  onClick={() => step.found && choose(step.device, step.path, step.found)}
                >Use this folder</button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
